// Duty of Care / compliance scoring — single source of truth.
//
// Used by the business Dashboard summary widget and the Compliance
// (Health Profile Completeness) page so both always show the same numbers.
//
// The model is intentionally simple and transparent: each profile field
// carries a weight, the per-employee score is the sum of weights present,
// and a workforce is "ready" when its critical emergency data is in place.
// This maps to the employer duty of care for travelling staff
// (EU OSH Framework Directive 89/391/EEC) and the ISO 31030 travel-risk
// management standard, which both expect employers to know that each
// worker is identified, reachable, insured and emergency-ready before travel.

// Each check: { label, weight, critical, check }
// `critical: true` marks data a clinician or first responder needs in an
// emergency — its absence is a duty-of-care gap, not just an incomplete form.
export const CHECKS = [
  { label: 'First & Last Name', weight: 15, check: (u) => !!(u.firstName && u.lastName) },
  { label: 'Email', weight: 10, check: (u) => !!u.email },
  { label: 'Phone', weight: 10, check: (u) => !!u.phone },
  { label: 'Date of Birth', weight: 10, check: (u) => !!(u.birthDate || u.dob) },
  { label: 'Gender', weight: 5, check: (u) => !!u.gender },
  { label: 'Nationality', weight: 5, check: (u) => !!u.nationality },
  { label: 'Postal Address', weight: 15, check: (u) => Array.isArray(u.postalAddress) && u.postalAddress.length > 0 },
  { label: 'Emergency Contact', weight: 15, critical: true, check: (u) => Array.isArray(u.emergencyContact) && u.emergencyContact.length > 0 },
  { label: 'Insurance Info', weight: 10, critical: true, check: (u) => Array.isArray(u.insurance) && u.insurance.length > 0 },
  { label: 'Card Ordered', weight: 5, check: (u) => u.cardOrderStatus === 'Registered' },
];

export const CRITICAL_CHECKS = CHECKS.filter((c) => c.critical);

// Readiness bands for a single employee score (0–100).
export const READINESS_BANDS = {
  ready: { min: 80, label: 'Ready', color: '#10b981' },
  partial: { min: 50, label: 'Partial', color: '#f59e0b' },
  atRisk: { min: 0, label: 'At risk', color: '#ef4444' },
};

export function calcScore(user) {
  return CHECKS.reduce((total, c) => total + (c.check(user) ? c.weight : 0), 0);
}

export function scoreColor(score) {
  if (score >= READINESS_BANDS.ready.min) return READINESS_BANDS.ready.color;
  if (score >= READINESS_BANDS.partial.min) return READINESS_BANDS.partial.color;
  return READINESS_BANDS.atRisk.color;
}

export function scoreBand(score) {
  if (score >= READINESS_BANDS.ready.min) return READINESS_BANDS.ready;
  if (score >= READINESS_BANDS.partial.min) return READINESS_BANDS.partial;
  return READINESS_BANDS.atRisk;
}

// An employee is missing critical data if any critical check fails.
export function missingCriticalLabels(user) {
  return CRITICAL_CHECKS.filter((c) => !c.check(user)).map((c) => c.label);
}

// Roll a list of employees up into the numbers the dashboard summary needs.
export function deriveReadiness(employees = []) {
  const total = employees.length;
  const scored = employees.map((e) => ({ ...e, score: calcScore(e) }));

  const sum = scored.reduce((s, e) => s + e.score, 0);
  const avg = total ? Math.round(sum / total) : 0;

  const ready = scored.filter((e) => e.score >= READINESS_BANDS.ready.min).length;
  const atRisk = scored.filter((e) => e.score < READINESS_BANDS.partial.min).length;
  const missingCritical = scored.filter((e) => missingCriticalLabels(e).length > 0).length;

  // Headline readiness = share of the workforce that is emergency-ready
  // (score in the "ready" band AND no missing critical fields).
  const compliant = scored.filter(
    (e) => e.score >= READINESS_BANDS.ready.min && missingCriticalLabels(e).length === 0,
  ).length;
  const readyPct = total ? Math.round((compliant / total) * 100) : 0;

  return { total, avg, ready, atRisk, missingCritical, compliant, readyPct, scored };
}
